/**
 * The Library's folders, as the web reads them: ONE endpoint, ONE query key.
 *
 * Decision 18 (2026-10-21): `ai_document_folders` is the one folder store and
 * the API's one folder route family is `/wiki/folders` (its reader and writer
 * are services/library/folder-store.ts). Every tenant screen that lists
 * folders — the Library sidebar and Move dialog, the upload dialog's picker,
 * the document detail page's folder select, the Favorites folder expansion,
 * the Ask Argus project Sources picker and the agent builder's Library
 * references — reads them HERE, so a folder created from the sidebar is the
 * same folder, with the same id, in every one of them (L18-06). No web file
 * outside this module issues the GET, names the fetcher or the query key:
 * readers use `useLibraryFolders` (or `readLibraryFolders` outside a render),
 * and a folder write calls `invalidateLibraryFolders` so every reader sees it
 * (library-folders-seam.lb2t3.test.ts).
 *
 * The System Library's folders are a different scope with their own route
 * (`/admin/platform/library/folders`, read through `useSysLibFolders` in
 * use-sysadmin-library.ts) — one set of folders there too, never a second
 * per-table list.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { FolderShape } from '@argusai/shared';

/** The one tenant folder route (GET lists, POST creates, PUT/DELETE /:id). */
export const LIBRARY_FOLDERS_PATH = '/wiki/folders';

/** The one query key for the tenant's folder list. */
export const libraryFoldersQueryKey = ['wiki', 'folders'] as const;

/**
 * A folder as GET /wiki/folders returns it — the shared Folder wire shape
 * (@argusai/shared document.schema.ts, LB3-T1). Its sensitivity and export
 * control are a FLOOR over every document beneath it (decision 37, C1), decided
 * in the API's Document visibility resolver — a surface enforces it once its
 * reader asks that resolver (Library LB-4); this screen only shows the values.
 */
export type LibraryFolder = FolderShape;

/**
 * The tenant's folders. A failed read REJECTS (the caller decides what an
 * error looks like); a response without the list is a failed read too, never
 * an empty Library.
 */
export async function fetchLibraryFolders(): Promise<LibraryFolder[]> {
  const res = await apiClient.get<{ data: LibraryFolder[] }>(LIBRARY_FOLDERS_PATH);
  if (!res || !Array.isArray(res.data)) {
    throw new Error('The folder list could not be read.');
  }
  return res.data;
}

/**
 * How long a folder list counts as fresh. Short, so a picker opened after a
 * folder was created elsewhere refetches instead of showing a five-minute-old
 * list (the app default, lib/query-client.ts); not zero, so two readers that
 * mount during one page load — the Documents page and its list pane — share
 * ONE request (LB-6 prove E1: "one request per list"). A folder write still
 * refetches at once through invalidateLibraryFolders.
 */
export const LIBRARY_FOLDERS_FRESH_MS = 5_000;

/** The one set of query options every hook reader uses. */
export const libraryFoldersQueryOptions = {
  queryKey: libraryFoldersQueryKey,
  queryFn: fetchLibraryFolders,
  staleTime: LIBRARY_FOLDERS_FRESH_MS,
} as const;

/** The tenant's folders as a query. */
export function useLibraryFolders(opts: { enabled?: boolean } = {}) {
  return useQuery<LibraryFolder[], Error>({
    ...libraryFoldersQueryOptions,
    enabled: opts.enabled ?? true,
  });
}

/**
 * The tenant's folders, read once through the shared query cache — for code
 * that needs the list inside an event or effect rather than as a hook (the
 * Favorites folder expansion). Same key, same fetcher, never a private read.
 */
export function readLibraryFolders(queryClient: QueryClient): Promise<LibraryFolder[]> {
  return queryClient.fetchQuery({ queryKey: libraryFoldersQueryKey, queryFn: fetchLibraryFolders, staleTime: 0 });
}

/** Every folder reader refetches — call after a folder is created, renamed or deleted. */
export function invalidateLibraryFolders(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: libraryFoldersQueryKey });
}
